import { Bugfender, LogLevel } from '@bugfender/sdk';
import { IconButton, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useBoolean } from 'usehooks-ts';

import { addSigningDocument } from '@/api/document';

import { Icon } from '@/components/Icon/Icon';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { notifyUserChanged } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';
import { notifyMinorDataChanged } from '@/redux/operation';
import { hideFileViewer } from '@/redux/ui';

import {
  AcrobatSignEventType,
  AcrobatSignMessage,
  BugfenderTagType,
  SigningDocument,
} from '@/types/document';
import { User } from '@/types/user';

import { EmailCopyField } from './EmailCopyField';

import {
  SigningDocumentContainer,
  SigningDocumentHeader,
  WebView,
} from './components.styled';

type SigningDocumentViewerProps = Readonly<{
  src: SigningDocument;
  onClose?: () => void;
  onSigned?: () => void;
  user?: {
    email?: User['email'];
    operationId?: User['operationId'];
    id?: User['id'];
  };
}>;

export function SigningDocumentViewer({
  src: { id, name, url, formName, docType, docTypeWithAccountType },
  onClose,
  onSigned,
  user: propsUser,
}: SigningDocumentViewerProps) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(selectLoggedInUser);
  const user = propsUser ?? loggedInUser;
  const hasReportedSigned = useRef(false);

  // In Right Start flow, the minor may not have an email — fall back to
  // the adult's email. In the adult flow, user IS loggedInUser so this is a no-op.
  const emailForSigning = user?.email || loggedInUser?.email;

  const refreshUserAfterSigning = () => {
    dispatch(notifyUserChanged());
    if (propsUser) {
      dispatch(notifyMinorDataChanged());
    }
  };

  const { value: isPresignNotifying, setFalse: hidePresignNotifying } =
    useBoolean(true);

  const {
    value: isConfirmingClose,
    setTrue: requestClose,
    setFalse: cancelClose,
  } = useBoolean();

  const {
    value: isCopied,
    setTrue: setCopied,
    setFalse: resetCopied,
  } = useBoolean(false);

  const { mutate } = useMutation({
    mutationFn: addSigningDocument,
    onSuccess: () => {
      refreshUserAfterSigning();
      setTimeout(() => {
        onSigned?.();
        dispatch(hideFileViewer());
      }, 5000);
      setTimeout(() => {
        refreshUserAfterSigning();
      }, 10000);
    },
  });

  useEffect(() => {
    const onBeforeUnload = () => t('moreInfo.signingDocViewer.unload');

    window.onbeforeunload = onBeforeUnload;

    return () => {
      window.onbeforeunload = null;
    };
  }, [t]);

  useEffect(() => {
    const onMessage = (message: MessageEvent<AcrobatSignMessage>) => {
      const payload =
        typeof message.data === 'string'
          ? JSON.parse(message.data)
          : message.data;

      const logPayload = {
        formId: id,
        formName: formName ?? name,
        docType: docTypeWithAccountType ?? docType ?? formName ?? name,
        clientOperationId: user?.operationId,
        clientEmail: user?.email ?? '',
      };

      if (payload.type === AcrobatSignEventType.Signed) {
        if (hasReportedSigned.current) return;

        hasReportedSigned.current = true;
        Bugfender.sendLog({
          tag: BugfenderTagType.DOCUMENT_SIGNING_FORM_SIGNED,
          level: LogLevel.Info,
          text: JSON.stringify(logPayload),
        });

        mutate({
          formId: id,
          docType: docTypeWithAccountType ?? docType ?? formName ?? name,
          ...(propsUser ? { clientId: propsUser.id } : {}),
        });
      }

      if (payload.type === AcrobatSignEventType.PageLoad) {
        Bugfender.sendLog({
          tag: BugfenderTagType.DOCUMENT_SIGNING_FORM_LOADED,
          level: LogLevel.Info,
          text: JSON.stringify(logPayload),
        });
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formName, id, mutate, name, onSigned, propsUser]);

  const handleCopy = () => {
    if (!emailForSigning) return;

    navigator.clipboard.writeText(emailForSigning);
    setCopied();
    setTimeout(resetCopied, 2000);
  };

  return (
    <>
      <SigningDocumentContainer>
        <SigningDocumentHeader>
          <IconButton onClick={requestClose}>
            <Icon name="close" color={theme => theme.palette.neutral[700]} />
          </IconButton>
          <Typography variant="bodyB1Strong">{name}</Typography>
        </SigningDocumentHeader>
        <WebView src={url} />
      </SigningDocumentContainer>
      {isPresignNotifying && emailForSigning && (
        <ConfirmDrawer
          title={t('moreInfo.signingDocViewer.copyDrawer.title')}
          description={
            <>
              {t('moreInfo.signingDocViewer.copyDrawer.description')}
              <EmailCopyField
                email={emailForSigning}
                onCopy={handleCopy}
                isCopied={isCopied}
              />
            </>
          }
          icon="email"
          primaryAction={hidePresignNotifying}
          primaryText={t('moreInfo.signingDocViewer.copyDrawer.primaryText')}
          onClose={hidePresignNotifying}
        />
      )}
      {isConfirmingClose && (
        <ConfirmDrawer
          title={t('moreInfo.signingDocViewer.closeConfirm.title')}
          description={t('moreInfo.signingDocViewer.closeConfirm.description')}
          primaryAction={onClose}
          primaryText={t('moreInfo.signingDocViewer.closeConfirm.primaryText')}
          secondaryAction={cancelClose}
          secondaryText={t(
            'moreInfo.signingDocViewer.closeConfirm.secondaryText',
          )}
          onClose={cancelClose}
        />
      )}
    </>
  );
}
